/**
 * A YouTube video link: the ONE parser for every link field in the app, and the links built from
 * its id. The YouTube rule (decided 2026-10-23): lenient input, canonical output.
 *
 * WHY ONE PARSER
 *   Sol's Save results stores the YouTube link a search ran with on the playlist
 *   (`search_metadata.videoLink`, owner's "Link + lead embed", 2026-10-23), and the share page
 *   embeds it. So what Sol, the classic search page, /home and the storefront's search dock take,
 *   what a playlist stores and what the pages show must come from one reading of a link. Before
 *   this, four copies disagreed: Sol's and the backend's search check were substring tests
 *   (`https://evil.example/?youtube.com/` passed), the storefront's search dock took any id, and
 *   VideoPreview missed /shorts/ and /live/ links, so a link Sol accepted showed a blank preview.
 *
 * INPUT: LENIENT, BECAUSE PEOPLE PASTE LINKS AS THEY FIND THEM
 *   - Scheme: https://, http:// or none at all (read as https://), case-blind. "youtu.be/<id>"
 *     and "http://" links worked in the search fields before the strict parser of 2026-10-23
 *     refused them; they work again.
 *   - Host, case-blind, with no trailing dot: youtube.com, www.youtube.com, m.youtube.com,
 *     music.youtube.com or youtu.be.
 *   - The id: /watch?v=<id> (the first v wins, even a blank one, which gives null; the query is
 *     read as the server reads it, so a "?" at its start is part of the first key, and
 *     "watch??v=<id>" has no v), youtu.be/<id>, or exactly /shorts/<id>, /embed/<id> or
 *     /live/<id>. It is exactly 11 characters of letters, digits, "_" and "-", and it is never
 *     percent-decoded out of the path.
 *   - Refused: whitespace or a control character anywhere (the ends are trimmed first); U+FEFF
 *     anywhere, the start included (trim() would quietly remove a leading one); a backslash; a
 *     user part, even an empty "@"; any ":" after the host (a port, even an empty one); "." and
 *     ".." path segments, also written %2e / %2E; more path than the form has, a trailing slash
 *     included; anything that isn't a string.
 *   The link is read as typed, by one regular expression, never by the browser's URL parser:
 *   that parser forgives what this refuses ("\" read as "/", tabs and line breaks dropped,
 *   "https:host" and "https:///host" read as "https://host", dot segments resolved).
 *
 * OUTPUT: CANONICAL
 *   A field keeps and sends `canonicalYouTubeUrl(id)` (https://www.youtube.com/watch?v=<id>),
 *   never the text it was given, and that is the only value a playlist ever stores. Every embed
 *   `src` is built from the id alone (`youTubeEmbedUrl`, youtube-nocookie.com/embed/<id>), so
 *   nothing in a pasted or stored string (tracking parameters, an injected attribute, a
 *   `javascript:` link) reaches an href or an iframe.
 *
 * THE SERVER IS STRICTER, ON PURPOSE
 *   The backend's `youtube_video_id` (services/playlist_video_service.py) checks STORED values,
 *   on every share-link resolve: https only, on youtube.com, www.youtube.com, m.youtube.com or
 *   youtu.be, with the same refusals. Stored values are canonical, so it never needs the forms
 *   only input takes: http://, no scheme and music.youtube.com. Those three are the only
 *   difference; on every other link the two parsers agree, and read the same id. The frontend
 *   reads a stored value with this parser too (`playlistVideoLink`), so only a row edited by hand
 *   into one of those three forms would show on the owner's page and not on the share page.
 */

const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "music.youtube.com",
]);
const SHORT_LINK_HOST = "youtu.be";
const BYTE_ORDER_MARK = "\ufeff";
// eslint-disable-next-line no-control-regex
const WHITESPACE_CONTROL_OR_BACKSLASH = /[\s\x00-\x1f\x7f-\x9f\\]/;
/**
 * A whole link as typed: an optional "https://" or "http://", a plain host (letters, digits, "."
 * and "-" only, so no user, no "@" and no ":"), then an optional path, query and fragment.
 */
const LINK = /^(?:https?:\/\/)?([A-Za-z0-9.-]+)(\/[^?#]*)?(?:\?([^#]*))?(?:#.*)?$/i;
const SHORT_LINK_PATH = /^\/([^/]+)$/;
const ID_PATH = /^\/(?:shorts|embed|live)\/([^/]+)$/;

/**
 * What a link field says when `youTubeVideoId` refuses a link. True for every refusal: it says
 * which links are taken, whatever was wrong with this one (a channel or a playlist, another host,
 * a port, a space inside). Sol, the classic search page, /home and the storefront's search dock
 * all say this.
 */
export const REFUSED_YOUTUBE_LINK_MESSAGE =
  "Paste a YouTube video's link, like youtube.com/watch?v=… or youtu.be/….";

/** True for an 11-character YouTube video id. */
export function isYouTubeVideoId(value: unknown): value is string {
  return typeof value === "string" && VIDEO_ID.test(value);
}

/** The 11-character video id of a YouTube video link, or null (see INPUT above). */
export function youTubeVideoId(url: unknown): string | null {
  if (typeof url !== "string" || url.includes(BYTE_ORDER_MARK)) return null;
  const value = url.trim();
  if (WHITESPACE_CONTROL_OR_BACKSLASH.test(value)) return null;
  const link = LINK.exec(value);
  if (!link) return null;

  const [, rawHost, path = "", query = ""] = link;
  const host = rawHost.toLowerCase();
  let id: string | null = null;
  // The path is compared as typed, so "/./watch", "/%2e%2e/<id>" and "/<id>/" never match.
  if (host === SHORT_LINK_HOST) {
    id = SHORT_LINK_PATH.exec(path)?.[1] ?? null;
  } else if (YOUTUBE_HOSTS.has(host)) {
    // "&" first, so a "?" at the start of the query stays part of its first key, as Python's
    // parse_qsl reads it on the server: URLSearchParams drops one, and took "watch??v=<id>".
    id =
      path === "/watch"
        ? new URLSearchParams(`&${query}`).get("v")
        : (ID_PATH.exec(path)?.[1] ?? null);
  }
  return isYouTubeVideoId(id) ? id : null;
}

function assertVideoId(id: string): void {
  if (!isYouTubeVideoId(id)) throw new Error("Not a YouTube video id.");
}

/** The link a playlist stores: `https://www.youtube.com/watch?v=<id>`. `id` must be a video id. */
export function canonicalYouTubeUrl(id: string): string {
  assertVideoId(id);
  return `https://www.youtube.com/watch?v=${id}`;
}

/** The share page's embed (YouTube's privacy-enhanced mode). `id` must be a video id. */
export function youTubeEmbedUrl(id: string): string {
  assertVideoId(id);
  return `https://www.youtube-nocookie.com/embed/${id}`;
}
